import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";
import useLanguageChange from "../../hooks/useLanguageChange";
import { setIsSlickViewOpen } from "../../reducers/common-reducer";
import { getLocalPartiName, getParticipantIndex } from "../../shared/dataTrackFunction";
import { isSilentObserver } from "../../shared/utility";
import { CALL_DATA } from "../../constants/PublicVariables";
import useParticipants from "../../hooks/useParticipants/useParticipants";
import { attachTracks, getSlickParticipantVideoContainer, getTracksByType, removeVideoElement } from "../Twilio/twilioCallUtility";
import { getVideoTracksByIdentity } from "../Twilio/VideoCall/VideoUtils";

const SlickView = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const remoteParticipants = useParticipants();

    const { isSlickViewOpen } = useSelector((state) => state.commonReducer);
    const { streamingPartiIdentity, videoStreamingUsers, screenSharingUsers } = useSelector((state) => state.twilioEvents);
    const { galleryViewConnectedUser } = useSelector((state) => state.galleryView);

    const [resetSlickSlider, setResetSlickSlider] = useState(0);

    const handleOnClick = () => {
        dispatch(setIsSlickViewOpen(!isSlickViewOpen));
    };

    useEffect(() => {
        const handleResize = () => {
            setTimeout(() => {
                setResetSlickSlider((prev) => prev + 1); // for re-render slick slider
                /* reset slick participant video element from the dom */
                otherUsersList?.forEach(function (otherParticipant) {
                    const remoteContainer = getSlickParticipantVideoContainer(otherParticipant.identity);
                    removeVideoElement(remoteContainer);
                });
            }, 100);
        };

        window.addEventListener("orientationchange", handleResize);

        return () => {
            window.removeEventListener("orientationchange", handleResize);
            dispatch(setIsSlickViewOpen(false));
        };
    }, []);

    const otherUsersList = useMemo(() => {
        let users = [];
        galleryViewConnectedUser.forEach((user) => {
            if (user.identity !== streamingPartiIdentity && !isSilentObserver(user.identity)) {
                let participantName = getParticipantIndex(user.identity);
                if (user.identity == CALL_DATA.localIdentity) {
                    if (CALL_DATA.participantUserName !== "") {
                        participantName = CALL_DATA.participantUserName + localisedValues?.you;
                    } else {
                        participantName = getLocalPartiName();
                    }
                }

                users.push({ ...user, participantName });
            }
        });

        return users;
    }, [galleryViewConnectedUser, streamingPartiIdentity, resetSlickSlider]);

    const preparedTracks = useMemo(() => {
        if (otherUsersList.length > 0) {
            const userStreamingArray = [];
            const isLocalVideoStreamingUser = videoStreamingUsers.includes(CALL_DATA.localIdentity); // Check that Local user video is on/off
            const isLocalScreenSharingUser = screenSharingUsers.includes(CALL_DATA.localIdentity); // Check that Local user screen share is on/off

            const localContainer = getSlickParticipantVideoContainer(CALL_DATA.localIdentity); // get the local participant element
            if (isLocalVideoStreamingUser || isLocalScreenSharingUser) {
                if (!localContainer?.querySelector("video")) {
                    // only attached the video element if it is not there
                    const trackArr = getVideoTracksByIdentity(CALL_DATA.localIdentity); // get local Video tracks
                    userStreamingArray.push({ identity: CALL_DATA.localIdentity, tracks: trackArr });
                }
            } else {
                removeVideoElement(localContainer); // remove local video element in DOM
            }
            remoteParticipants?.forEach(function (remoteParticipant) {
                const remoteContainer = getSlickParticipantVideoContainer(remoteParticipant.identity);
                if (remoteParticipant.videoTracks.size > 0) {
                    if (!remoteContainer?.querySelector("video")) {
                        // only attached the video element if it is not there
                        const trackArr = getTracksByType(remoteParticipant, "video"); // get remote Video tracks
                        userStreamingArray.push({ identity: remoteParticipant.identity, tracks: trackArr });
                    }
                } else {
                    removeVideoElement(remoteContainer); // remove remote video element in DOM
                }
            });
            return userStreamingArray;
        }
    }, [otherUsersList, remoteParticipants, videoStreamingUsers, screenSharingUsers]);

    useEffect(() => {
        if (preparedTracks?.length > 0) {
            preparedTracks.forEach(function ({ tracks, identity }) {
                const container = getSlickParticipantVideoContainer(identity);
                if (container) {
                    attachTracks(tracks, container); // attach the video in slick participant element
                }
            });
        }
    }, [preparedTracks]);

    const settings = useMemo(() => {
        return {
            dots: false,
            infinite: false,
            speed: 300,
            slidesToShow: 5,
            slidesToScroll: 1,
            draggable: false,
            responsive: [
                {
                    breakpoint: 1210,
                    settings: {
                        slidesToShow: 5,
                        slidesToScroll: 1,
                        touchMove: otherUsersList.length > 5, // don't allow user to scroll or move while slidesToShow count is less then other user list
                    },
                },
                {
                    breakpoint: 876,
                    settings: {
                        slidesToShow: 5,
                        slidesToScroll: 1,
                        touchMove: otherUsersList.length > 5, // don't allow user to scroll or move while slidesToShow count is less then other user list
                    },
                },
                {
                    breakpoint: 481,
                    settings: {
                        slidesToShow: 3,
                        slidesToScroll: 1,
                        touchMove: otherUsersList.length > 3, // don't allow user to scroll or move while slidesToShow count is less then other user list
                    },
                },
            ],
        };
    }, [otherUsersList]);

    return (
        <div className={clsx("gallery-pin-view", isSlickViewOpen && "active")}>
            <div className='gallery-pin-view-list'>
                <div className='gallery-pin-view-in'>
                    <div className='gallery-pin-view-slider'>
                        <div className='slider-container'>
                            <Slider {...settings} key={resetSlickSlider}>
                                {otherUsersList.map((user) => {
                                    const isUserStreaming = user.isVideoStreaming || user.isScreenShareStreaming;
                                    return (
                                        <div className='gallery-pin-item' key={user.identity}>
                                            <div className='gallery-pin-item-in'>
                                                <div className='gallery-pin-view-box'>
                                                    {isUserStreaming ? (
                                                        <div className='gallery-pin-video' id={`slickVideo_${user?.identity}`}></div>
                                                    ) : (
                                                        <div className='pin-participant-novideo'>
                                                            <div className='pin-participant-novideo-in'>
                                                                <div className='pin-participant-usericon'>
                                                                    <span className='for-icon icon-user'></span>
                                                                </div>
                                                                <span className='for-bg'></span>
                                                                <span className='for-border'></span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className='pin-participant-name'>
                                                        <span className='name'>{user.participantName}</span>
                                                        {!isUserStreaming && <span className='for-icon icon-video-off'></span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Slider>
                        </div>
                    </div>
                </div>
            </div>
            <div className='gallery-pin-button'>
                <button className='btn btn-primary gallery-pin-btn' onClick={handleOnClick}>
                    <span className='btn-text'>{isSlickViewOpen ? localisedValues["hide_stream"] : localisedValues["show_stream"]}</span>
                    <span className='btn-icon icon-down-arrow'></span>
                </button>
            </div>
        </div>
    );
};

export default SlickView;
